import { useEffect, useMemo, useState } from "react";
import { MdInsights } from "react-icons/md";

import { ChartSkeleton } from "components/charts/ChartSkeleton";
import LineChart, { LineChartSeries } from "components/charts/LineChart";
import {
  Box,
  Flex,
  Grid,
  HStack,
  InfoBox,
  KpiTile,
  Select,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useAiCosts } from "hooks/useAiCosts";
import { useTranslation } from "hooks/useTranslation";
import {
  AiCostBreakdownItem,
  AiCostPlaneValue,
  AiCostSummary,
  AiReportRange,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import { formatUsd, formatWindow } from "./usageFormat";

interface CostsPanelProps {
  /**
   * The Dashboard's shared date range. When given, the panel drops its own Period select and
   * labels the tiles with the effective window the server echoes back.
   */
  range?: AiReportRange;
  /** Controlled plane — the page owns the Usage select; the panel hides its own. */
  plane?: AiCostPlaneValue;
  /** Bumped by the page's Refresh button. Re-runs the query without remounting the panel. */
  refreshToken: number;
  /** Reports the query state so the host can show a refresh spinner in its own card header. */
  onLoadingChange?: (loading: boolean) => void;
}

/** The server clamps to 1–90; these are the three windows worth offering. */
const WINDOWS = [7, 30, 90];

const formatDayLabel = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });

/**
 * `byDay` is **sparse** — a day with no traffic is absent, not zero — so plotting it directly
 * draws a straight line between two active days and hides every quiet one. This walks the whole
 * requested window and fills the gaps.
 *
 * The window comes off `summary.from`/`summary.to` rather than the requested `days`, because the
 * server clamps the request and only the response knows what it actually answered. `to` is
 * exclusive as a timestamp but its calendar day is a real (partial) bucket, so it is included.
 */
const densifyDaily = (summary: AiCostSummary) => {
  const byDate = new Map(
    summary.byDay.map((point) => [point.date.slice(0, 10), point]),
  );
  const lastKey = summary.to.slice(0, 10);
  const cursor = new Date(`${summary.from.slice(0, 10)}T00:00:00Z`);
  const points: Array<{ x: string; y: number }> = [];

  // Bounded by the server's own 90-day cap; the guard is against a malformed range, not the cap.
  for (let day = 0; day <= 91; day += 1) {
    const key = cursor.toISOString().slice(0, 10);
    points.push({ x: formatDayLabel(key), y: byDate.get(key)?.costUsd ?? 0 });
    if (key >= lastKey) break;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return points;
};

/**
 * Cost attribution — what the gateway spent, split by model, route, widget and day.
 *
 * Defaults to the Tenant plane. Platform-plane calls are StrikeUp's own overhead (server
 * generation, guardrail authoring, demo data) and are not billed to the tenant, so folding them
 * into one number would overstate what this workspace costs. `All` is every row, which is useful
 * for reconciling against a provider invoice and misleading for anything else.
 */
const CostsPanel = ({
  refreshToken,
  range,
  plane: controlledPlane,
  onLoadingChange,
}: CostsPanelProps) => {
  const { t } = useTranslation();
  const [days, setDays] = useState(WINDOWS[1]);
  const [ownPlane, setPlane] = useState<AiCostPlaneValue>(
    AiCostPlaneValue.Tenant,
  );
  const plane = controlledPlane ?? ownPlane;
  const { summary, loading } = useAiCosts(days, plane, refreshToken, range);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const series: LineChartSeries[] = useMemo(() => {
    if (!summary || summary.byDay.length === 0) return [];
    return [
      {
        id: "cost",
        label: t("app.aiGateway.costs.chart.series"),
        data: densifyDaily(summary),
      },
    ];
  }, [summary, t]);

  const windowItems = WINDOWS.map((value) => ({
    value: String(value),
    label: t("app.aiGateway.costs.window.option", { days: value }),
  }));

  const planeItems = [
    {
      value: String(AiCostPlaneValue.Tenant),
      label: t("app.aiGateway.costs.plane.tenant"),
    },
    {
      value: String(AiCostPlaneValue.Platform),
      label: t("app.aiGateway.costs.plane.platform"),
    },
    {
      value: String(AiCostPlaneValue.All),
      label: t("app.aiGateway.costs.plane.all"),
    },
  ];

  /**
   * An empty key means the underlying column was null — a call made outside any widget, or one
   * whose route was never tagged. The server sends no label for it because it has no locale.
   */
  const rowLabel = (item: AiCostBreakdownItem) =>
    item.label?.trim() ||
    item.key?.trim() ||
    t("app.aiGateway.costs.unattributed");

  const renderBreakdown = (
    titleKey: string,
    nameHeaderKey: string,
    items: AiCostBreakdownItem[],
  ) => (
    <VStack align="stretch" gap={2}>
      <Text fontSize="sm" fontWeight="semibold">
        {t(titleKey)}
      </Text>
      {items.length === 0 ? (
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.costs.noBreakdown")}
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="620px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>{t(nameHeaderKey)}</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.calls")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.inputTokens")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.outputTokens")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.cost")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={`${item.key}-${item.label}`}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm">{rowLabel(item)}</Text>
                      {item.unpricedCalls > 0 && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.aiGateway.costs.table.unpricedRow", {
                            count: item.unpricedCalls,
                          })}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {item.calls.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {item.inputTokens.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {item.outputTokens.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {formatUsd(item.costUsd)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );

  const windowLabel = range
    ? formatWindow(
        summary?.from ?? range.startDate,
        summary?.to ?? range.endDate,
      )
    : t("app.aiGateway.costs.kpi.window", { days: summary?.days ?? days });

  return (
    <VStack align="stretch" gap={5}>
      <Flex justify="space-between" align="flex-end" wrap="wrap" gap={3}>
        <Text
          fontSize="sm"
          color={getThemeColor("text.secondary")}
          maxW="560px"
        >
          {t("app.aiGateway.costs.subtitle")}
        </Text>
        <HStack gap={3} wrap="wrap">
          {!range && (
            <Select
              label={t("app.aiGateway.costs.window.label")}
              items={windowItems}
              value={[String(days)]}
              onValueChange={(d: { value: string[] }) =>
                setDays(Number(d.value[0]))
              }
              size="sm"
              w="170px"
            />
          )}
          {controlledPlane === undefined && (
            <Select
              label={t("app.aiGateway.costs.plane.label")}
              items={planeItems}
              value={[String(plane)]}
              onValueChange={(d: { value: string[] }) =>
                setPlane(Number(d.value[0]) as AiCostPlaneValue)
              }
              size="sm"
              w="200px"
            />
          )}
        </HStack>
      </Flex>

      <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
        <KpiTile
          label={t("app.aiGateway.costs.kpi.totalCost")}
          value={summary ? formatUsd(summary.totalCostUsd) : "—"}
          sublabel={windowLabel}
        />
        <KpiTile
          label={t("app.aiGateway.costs.kpi.calls")}
          value={summary ? summary.totalCalls.toLocaleString() : "—"}
          sublabel={windowLabel}
        />
        <KpiTile
          tone="flat"
          label={t("app.aiGateway.costs.kpi.inputTokens")}
          value={summary ? summary.totalInputTokens.toLocaleString() : "—"}
          sublabel={windowLabel}
        />
        <KpiTile
          tone="flat"
          label={t("app.aiGateway.costs.kpi.outputTokens")}
          value={summary ? summary.totalOutputTokens.toLocaleString() : "—"}
          sublabel={windowLabel}
        />
      </Grid>

      {summary && summary.unpricedCalls > 0 && (
        <InfoBox variant="warning">
          <Text fontSize="sm">
            {t("app.aiGateway.costs.unpricedNotice", {
              count: summary.unpricedCalls,
            })}
          </Text>
        </InfoBox>
      )}

      <VStack align="stretch" gap={2}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.costs.chart.title")}
        </Text>
        {loading && !summary ? (
          <ChartSkeleton height={280} withLegend />
        ) : series.length > 0 ? (
          <LineChart
            series={series}
            height={280}
            stacked={false}
            enableArea
            enableSlices
            xAxisLabel={t("app.aiGateway.costs.chart.axisDate")}
            yAxisLabel={t("app.aiGateway.costs.chart.axisCost")}
          />
        ) : (
          <VStack gap={3} py={10}>
            <Box color={getThemeColor("text.secondary")} fontSize="3xl">
              <MdInsights />
            </Box>
            <Text color={getThemeColor("text.secondary")} textAlign="center">
              {t("app.aiGateway.costs.empty")}
            </Text>
          </VStack>
        )}
      </VStack>

      {summary && (
        <>
          {renderBreakdown(
            "app.aiGateway.costs.byModel",
            "app.aiGateway.costs.table.model",
            summary.byModel,
          )}
          {renderBreakdown(
            "app.aiGateway.costs.byRoute",
            "app.aiGateway.costs.table.route",
            summary.byRoute,
          )}
          {renderBreakdown(
            "app.aiGateway.costs.byWidget",
            "app.aiGateway.costs.table.widget",
            summary.byWidget,
          )}
        </>
      )}
    </VStack>
  );
};

export default CostsPanel;
